import { useState } from "react";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, mediaUrl } from "@/lib/api";

export const PageTitle = ({ title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <h1 className="font-serif text-3xl sm:text-4xl" data-testid="admin-page-title">{title}</h1>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
    variant === "primary" ? "bg-[#5E6B52] text-white hover:bg-[#4A5540]" : variant === "danger" ? "bg-[#C84B31] text-white hover:bg-[#a83d27]"
      : variant === "accent" ? "bg-[#C86D51] text-white hover:bg-[#B25C42]" : "border border-border bg-white hover:bg-[#F3EDE2]"} ${className}`}>
    {children}
  </button>
);

export const Card = ({ children, className = "", ...p }) => (
  <div {...p} className={`rounded-2xl border border-border bg-white p-5 sm:p-6 ${className}`}>{children}</div>
);

export function MediaInput({ value, onChange, testid, accept = "image/*,video/*", onType }) {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/media", fd);
      onChange(r.data.url);
      onType?.(r.data.type, r.data.url);
      toast.success("Archivo subido");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 items-center">
      {value && !/\.(mp4|mov|webm)$/i.test(value) && <img src={mediaUrl(value)} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL o sube un archivo" data-testid={testid} />
      <label className="shrink-0 cursor-pointer rounded-full border border-border bg-white px-3 py-2 text-sm hover:bg-[#F3EDE2]" data-testid={`${testid}-upload`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <input type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
}

export function Field({ f, value, onChange, testid }) {
  const t = f.type || "text";
  let el;
  if (t === "textarea") el = <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} />;
  else if (t === "media") el = <MediaInput value={value} onChange={onChange} testid={testid} />;
  else if (t === "switch") el = <div><Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} /></div>;
  else if (t === "select") el = (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
      {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  else if (t === "multi") el = (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {f.options.map(([v, l]) => {
        const on = (value || []).includes(v);
        return <button type="button" key={v} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...(value || []), v])}
          className={`rounded-full px-3 py-1 text-xs border ${on ? "bg-[#5E6B52] text-white border-[#5E6B52]" : "bg-white border-border"}`}>{l}</button>;
      })}
      <span className="text-xs text-muted-foreground self-center">(ninguna = todas)</span>
    </div>
  );
  else el = <Input type={t} value={value ?? ""} onChange={(e) => onChange(t === "number" ? Number(e.target.value) : e.target.value)} data-testid={testid} />;
  return (
    <div className={f.wide ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground">{f.label}</Label>
      <div className="mt-1">{el}</div>
    </div>
  );
}

export function ListEditor({ items = [], onChange, fields, newItem, testid, addLabel = "Agregar" }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => {
    const n = [...items];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="space-y-4" data-testid={testid}>
      {items.map((it, i) => (
        <div key={it.id || i} className="rounded-2xl border border-border bg-[#FAF7F2] p-4" data-testid={`${testid}-item-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => <Field key={f.key} f={f} value={it[f.key]} onChange={(v) => upd(i, f.key, v)} testid={`${testid}-${i}-${f.key}`} />)}
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button type="button" onClick={() => move(i, -1)} className="p-2 rounded-full hover:bg-white" aria-label="Subir"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => move(i, 1)} className="p-2 rounded-full hover:bg-white" aria-label="Bajar"><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-${i}-delete`}
              className="p-2 rounded-full text-[#C84B31] hover:bg-white" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <Btn variant="outline" type="button" onClick={() => onChange([...items, { id: crypto.randomUUID(), ...newItem }])} data-testid={`${testid}-add`}>
        <Plus className="h-4 w-4" />{addLabel}
      </Btn>
    </div>
  );
}
